import type { ResidentDefaultFormValues } from '@/features/resident/types'
import {
  getMissingDefaultRequiredFields,
  isReimbursementRequestDateAfterLeaseEnd,
  needsDefaultRequiredFields,
  REIMBURSEMENT_REQUEST_DATE_AFTER_LEASE_END_MESSAGE,
} from '@/features/resident/utils/residentValidation'
import type { ReimbursementMilestoneId } from '../types/reimbursementFormTypes'
import type { ReimbursementFormLogicInput } from '../types/reimbursementFormTypes'
import {
  getCoreSectionDocTypes,
  getFinalSectionDocTypes,
  getUniqueSectionDocTypes,
  isCoreFieldsComplete,
  isCoreSectionSubmitReady,
  isFinalSectionFieldsComplete,
  isFinalSectionSubmitReady,
  isUniqueFieldsComplete,
  isUniqueSectionSubmitReady,
  sectionDocsAcceptableForSubmit,
} from './reimbursementFormLogic'
import type { ResidentDocumentGroup } from '@/features/resident/types'

const CORE_REQUIRED_WHEN_APPLICABLE: Array<keyof ResidentDefaultFormValues> = [
  'first_month_not_paid_month',
  'first_month_not_paid_year',
]

const UNIQUE_REQUIRED_WHEN_APPLICABLE: Array<keyof ResidentDefaultFormValues> = [
  'reason_for_default',
  'covid_19',
]

export type ValidateReimbursementSectionContext = {
  roleName: string
  selectedTab: string
  leaseEndDate?: string | null
}

export type ValidateReimbursementSectionResult =
  | { ok: true }
  | { ok: false; message: string }

function missingMappedRequiredFields(
  values: ResidentDefaultFormValues,
  roleName: string,
  selectedTab: string,
  fields: Array<keyof ResidentDefaultFormValues>,
): Array<keyof ResidentDefaultFormValues> {
  if (!needsDefaultRequiredFields(roleName, selectedTab, values.reimbursement_type)) {
    return []
  }
  return getMissingDefaultRequiredFields(values, roleName, selectedTab).filter((field) =>
    fields.includes(field),
  )
}

function validateCoreSection(
  input: ReimbursementFormLogicInput,
  values: ResidentDefaultFormValues,
  context: ValidateReimbursementSectionContext,
  residentDocuments?: ResidentDocumentGroup | null,
): ValidateReimbursementSectionResult {
  if (!isCoreFieldsComplete(input)) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  const missingScalars = missingMappedRequiredFields(
    values,
    context.roleName,
    context.selectedTab,
    CORE_REQUIRED_WHEN_APPLICABLE,
  )
  if (missingScalars.length > 0) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  const leaseEnd = context.leaseEndDate
  if (
    leaseEnd &&
    values.rental_status_date &&
    isReimbursementRequestDateAfterLeaseEnd(
      values.reimbursement_type,
      values.rental_status_date,
      String(leaseEnd),
    )
  ) {
    return { ok: false, message: REIMBURSEMENT_REQUEST_DATE_AFTER_LEASE_END_MESSAGE }
  }
  if (
    !sectionDocsAcceptableForSubmit(
      input,
      getCoreSectionDocTypes(input),
      residentDocuments,
    )
  ) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  return { ok: true }
}

function validateUniqueSection(
  input: ReimbursementFormLogicInput,
  values: ResidentDefaultFormValues,
  context: ValidateReimbursementSectionContext,
  residentDocuments?: ResidentDocumentGroup | null,
): ValidateReimbursementSectionResult {
  if (!isUniqueFieldsComplete(input)) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  const missingScalars = missingMappedRequiredFields(
    values,
    context.roleName,
    context.selectedTab,
    UNIQUE_REQUIRED_WHEN_APPLICABLE,
  )
  if (missingScalars.length > 0) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  if (
    !sectionDocsAcceptableForSubmit(
      input,
      getUniqueSectionDocTypes(input),
      residentDocuments,
    )
  ) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  return { ok: true }
}

function validateFinalSection(
  input: ReimbursementFormLogicInput,
  residentDocuments?: ResidentDocumentGroup | null,
): ValidateReimbursementSectionResult {
  if (!isFinalSectionFieldsComplete(input)) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  if (
    !sectionDocsAcceptableForSubmit(
      input,
      getFinalSectionDocTypes(input),
      residentDocuments,
    )
  ) {
    return { ok: false, message: 'Required Fields are Missing' }
  }
  return { ok: true }
}

/** Section-only submit validation (fields, mapped Angular required scalars, docs). */
export function validateReimbursementSectionForSubmit(
  milestoneId: ReimbursementMilestoneId,
  input: ReimbursementFormLogicInput,
  values: ResidentDefaultFormValues,
  context: ValidateReimbursementSectionContext,
  residentDocuments?: ResidentDocumentGroup | null,
): ValidateReimbursementSectionResult {
  if (milestoneId === 1) {
    return validateCoreSection(input, values, context, residentDocuments)
  }
  if (milestoneId === 2) {
    return validateUniqueSection(input, values, context, residentDocuments)
  }
  if (milestoneId === 3) {
    return validateFinalSection(input, residentDocuments)
  }
  return { ok: false, message: 'Invalid reimbursement section.' }
}

/** Mirrors submit-ready UI without duplicating logic. */
export function isReimbursementSectionSubmitReady(
  milestoneId: ReimbursementMilestoneId,
  input: ReimbursementFormLogicInput,
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  if (milestoneId === 1) {
    return isCoreSectionSubmitReady(input, residentDocuments)
  }
  if (milestoneId === 2) {
    return isUniqueSectionSubmitReady(input, residentDocuments)
  }
  if (milestoneId === 3) {
    return isFinalSectionSubmitReady(input, residentDocuments)
  }
  return false
}
